// Browser-compatible HMAC-SHA256 implementation
async function createHmac(_algorithm: string, key: string) {
  return {
    update: async (data: string) => {
      const encoder = new TextEncoder();
      const keyData = encoder.encode(key);
      const dataBytes = encoder.encode(data);
      
      // Import crypto-js for HMAC-SHA256
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );
      
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, dataBytes);
      const hashArray = Array.from(new Uint8Array(signature));
      
      return {
        digest: (encoding: string) => {
          if (encoding === 'hex') {
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          }
          return hashArray.map(b => String.fromCharCode(b)).join('');
        }
      };
    }
  };
}

export interface ShopifyAuthParams {
  shop: string;
  code?: string;
  state?: string;
  hmac?: string;
}

export interface ShopifyTokenResponse {
  access_token: string;
  scope: string;
  user_id: number;
  expires_in: number;
}

class ShopifyAuthService {
  private clientId = import.meta.env.VITE_SHOPIFY_CLIENT_ID;
  private clientSecret = import.meta.env.VITE_SHOPIFY_CLIENT_SECRET;
  private scopes = import.meta.env.VITE_SHOPIFY_SCOPES;
  private appUrl = import.meta.env.VITE_APP_URL;

  /**
   * Generate OAuth URL for Shopify app installation
   */
  generateAuthUrl(shop: string, state?: string): string {
    const nonce = state || this.generateNonce();
    const redirectUri = `${this.appUrl}/auth/callback`;
    
    const params = new URLSearchParams({
      client_id: this.clientId,
      scope: this.scopes,
      redirect_uri: redirectUri,
      state: nonce,
      'grant_options[]': 'per-user'
    });

    return `https://${shop}/admin/oauth/authorize?${params.toString()}`;
  }

  /**
   * Verify HMAC signature from Shopify
   */
  async verifyHmac(params: Record<string, string>, hmac: string): Promise<boolean> {
    const queryString = Object.keys(params)
      .filter(key => key !== 'hmac' && key !== 'signature')
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');

    const hmacInstance = await createHmac('sha256', this.clientSecret);
    const calculatedHmac = await hmacInstance.update(queryString);
    const digest = calculatedHmac.digest('hex');

    return digest === hmac;
  }

  /**
   * Exchange authorization code for access token
   */
  async exchangeCodeForToken(shop: string, code: string): Promise<ShopifyTokenResponse> {
    const response = await fetch(`https://${shop}/admin/oauth/access_token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: this.clientId,
        client_secret: this.clientSecret,
        code,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to exchange code for token: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Generate a random nonce for state parameter
   */
  private generateNonce(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Get shop domain from URL
   */
  getShopDomain(shopUrl: string): string {
    const url = new URL(shopUrl);
    return url.hostname;
  }

  /**
   * Validate shop URL format
   */
  isValidShopUrl(shopUrl: string): boolean {
    try {
      const url = new URL(shopUrl);
      return url.hostname.endsWith('.myshopify.com') || url.hostname.endsWith('.shopify.com');
    } catch {
      return false;
    }
  }
}

export const shopifyAuth = new ShopifyAuthService(); 